import { useRef, useEffect, useCallback } from "react";
import { Cell, TableEditorConfig } from "../dto";
import { useSettlementStore } from "../store";
import { assign } from "lodash";
import { getCellsBySelection } from "../utils";
import { useLatest } from "ahooks";

// 撤销重做&复制粘贴
const useTools = () => {
  const [{ config, selection }, { setConfig, setSelection }] =
    useSettlementStore();
  const configRef = useLatest(config);
  const selectionRef = useLatest(selection);
  // 撤销历史记录
  const undoRef = useRef<TableEditorConfig[]>([]);
  // 重做历史记录
  const redoRef = useRef<TableEditorConfig[]>([]);
  // 是否开始收集历史记录
  const startCollectRef = useRef(false);
  // 复制选项
  const copyRef = useRef<Cell>();

  useEffect(() => {
    return () => {
      if (!startCollectRef.current) {
        return;
      }
      startCollectRef.current = false;
      redoRef.current = [];
      undoRef.current.push(config);
      if (undoRef.current.length > 10) {
        undoRef.current.shift();
      }
    };
  }, [JSON.stringify(config)]);

  useEffect(() => {
    const toolsEvent = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (e.key === "z" && isCmdOrCtrl) {
        // 重做
        if (e.shiftKey && redoRef.current.length > 0) {
          const cfg = redoRef.current.pop();
          undoRef.current.push(configRef.current);
          setConfig(cfg);
          setSelection({ cellIds: [] });
        } else if (undoRef.current.length > 0) {
          // 撤销
          const cfg = undoRef.current.pop();
          redoRef.current.push(configRef.current);
          setConfig(cfg);
          setSelection({ cellIds: [] });
        }
      }
      // 复制
      if (e.key === "c" && isCmdOrCtrl) {
        const cells = getCellsBySelection(
          configRef.current,
          selectionRef.current
        );
        if (cells.length === 1) {
          copyRef.current = cells[0].cell;
        } else {
          copyRef.current = undefined;
        }
      }
      // 粘贴
      if (e.key === "v" && isCmdOrCtrl) {
        const activeElement = document.activeElement as HTMLTableCellElement;
        const cells = getCellsBySelection(
          configRef.current,
          selectionRef.current
        );
        if (cells.length === 1 && copyRef.current) {
          const [{ rowIndex, colIndex, cell }] = cells;
          // 粘贴时如果单元格没有聚焦则不执行粘贴操作
          if (cell.id !== activeElement?.dataset?.id) return;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, rowSpan, colSpan, ...rest } = copyRef.current;
          startCollectRef.current = true;
          setConfig((cfg) => {
            assign(cfg.rows[rowIndex].cells[colIndex], rest);
          });
        }
      }
    };
    document.addEventListener("keydown", toolsEvent);
    return () => {
      document.removeEventListener("keydown", toolsEvent);
    };
  }, []);

  return useCallback(() => {
    startCollectRef.current = true;
  }, []);
};

export default useTools;
